import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit,
    Trash2,
    Calendar,
    Building2,
    MapPin,
    Users,
    CheckCircle,
    XCircle,
    Image as ImageIcon,
    Clock,
    AlertCircle
} from 'lucide-react';
import { useGetApartmentQuery, useDeleteApartmentMutation, Apartment, ApartmentAvailability } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';

const ViewApartment: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const apartmentId = id ? parseInt(id) : null;
    const isValidId = apartmentId && !isNaN(apartmentId) && apartmentId > 0;

    const { data: apartmentResponse, isLoading, error, refetch } = useGetApartmentQuery(apartmentId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true
    });
    const [deleteApartment, { isLoading: isDeleting }] = useDeleteApartmentMutation();

    const apartment: Apartment | undefined = apartmentResponse?.data;

    const handleDelete = async () => {
        if (!apartment || !window.confirm('Are you sure you want to delete this apartment? This action cannot be undone.')) {
            return;
        }

        try {
            await deleteApartment(apartment.apartment_id).unwrap();
            toast.success('Apartment deleted successfully!');
            navigate('/admin/apartments');
        } catch (error: any) {
            console.error('Failed to delete apartment:', error);
            if (error?.status === 400) {
                toast.error(error?.data?.message || 'Cannot delete apartment with active bookings');
            } else {
                toast.error('Failed to delete apartment');
            }
        }
    };

    const getStatusColor = (isActive: boolean) => {
        return isActive
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800';
    };

    const getAvailabilityColor = (isAvailable: boolean) => {
        return isAvailable
            ? 'bg-blue-100 text-blue-800'
            : 'bg-red-100 text-red-800';
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    const formatDateTime = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading apartment details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Apartment</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the apartment details. Please try again.'}
                    </p>
                    <div className="space-x-4">
                        <button
                            onClick={() => refetch()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                        <button
                            onClick={() => navigate('/admin/apartments')}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            ← Back to Apartments
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!apartment) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Apartment Not Found</h2>
                    <p className="text-gray-600 mb-6">The apartment you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/apartments')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Apartments
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/apartments')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{apartment.name}</h1>
                                <p className="text-gray-600">Apartment Details & Information</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(apartment.is_active)}`}>
                                {apartment.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getAvailabilityColor(apartment.is_available)}`}>
                                {apartment.is_available ? 'Available' : 'Unavailable'}
                            </span>
                            <button
                                onClick={() => navigate(`/admin/apartments/edit/${apartment.apartment_id}`)}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Images */}
                            {apartment.images && apartment.images.length > 0 && (
                                <div className="bg-white rounded-lg shadow">
                                    <div className="px-6 py-4 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                            <ImageIcon className="w-5 h-5 mr-2" />
                                            Images
                                        </h2>
                                    </div>
                                    <div className="p-6">
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            {apartment.images.map((image, index) => (
                                                <div key={index} className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
                                                    <img
                                                        src={image}
                                                        alt={`${apartment.name} ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Basic Information */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Building2 className="w-5 h-5 mr-2" />
                                        Basic Information
                                    </h2>
                                </div>
                                <div className="p-6 space-y-6">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{apartment.name}</h3>
                                        {apartment.description && (
                                            <p className="text-gray-700 leading-relaxed">{apartment.description}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
                                        <div>
                                            <h6 className="font-medium text-gray-900 mb-2 flex items-center">
                                                <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                                Location
                                            </h6>
                                            <p className="text-sm text-gray-600">{apartment.location}</p>
                                            {apartment.address && (
                                                <p className="text-sm text-gray-500 mt-1">{apartment.address}</p>
                                            )}
                                        </div>
                                        <div>
                                            <h6 className="font-medium text-gray-900 mb-2 flex items-center">
                                                <Users className="w-4 h-4 mr-2 text-gray-400" />
                                                Capacity
                                            </h6>
                                            <p className="text-sm text-gray-600">{apartment.capacity} guests</p>
                                        </div>
                                        {apartment.amenities && (
                                            <div className="md:col-span-2">
                                                <h6 className="font-medium text-gray-900 mb-2">Amenities</h6>
                                                <p className="text-sm text-gray-600">{apartment.amenities}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Availability Information */}
                            {apartment.availabilities && apartment.availabilities.length > 0 && (
                                <div className="bg-white rounded-lg shadow">
                                    <div className="px-6 py-4 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                            <Calendar className="w-5 h-5 mr-2" />
                                            Availability & Blocked Dates
                                        </h2>
                                    </div>
                                    <div className="p-6">
                                        <div className="space-y-4">
                                            {apartment.availabilities
                                                .filter((a: ApartmentAvailability) => !a.is_available)
                                                .map((availability: ApartmentAvailability) => (
                                                    <div key={availability.availability_id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                                                        <div className="flex items-center">
                                                            <XCircle className="w-5 h-5 text-red-600 mr-3" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {formatDate(availability.date)}
                                                                </p>
                                                                {availability.reason && (
                                                                    <p className="text-sm text-gray-600 mt-1">
                                                                        {availability.reason}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="text-xs text-gray-500">
                                                            {formatDateTime(availability.created_at)}
                                                        </span>
                                                    </div>
                                                ))}
                                            {apartment.availabilities.filter((a: ApartmentAvailability) => !a.is_available).length === 0 && (
                                                <div className="text-center py-8">
                                                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                                                    <p className="text-gray-600">No blocked dates</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Quick Stats */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">Quick Stats</h2>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">Status</span>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(apartment.is_active)}`}>
                                            {apartment.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">Availability</span>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAvailabilityColor(apartment.is_available)}`}>
                                            {apartment.is_available ? 'Available' : 'Unavailable'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Capacity</span>
                                        <span className="text-gray-900 font-medium">{apartment.capacity}</span>
                                    </div>
                                    {apartment.bookings_count !== undefined && apartment.bookings_count > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Bookings</span>
                                            <span className="text-gray-900 font-medium">{apartment.bookings_count}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Clock className="w-5 h-5 mr-2" />
                                        Timeline
                                    </h2>
                                </div>
                                <div className="p-6 space-y-3">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Created</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {formatDateTime(apartment.created_at)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {formatDateTime(apartment.updated_at)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
                                </div>
                                <div className="p-6 space-y-3">
                                    <button
                                        onClick={() => navigate(`/admin/apartments/edit/${apartment.apartment_id}`)}
                                        className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        <Edit className="w-4 h-4 mr-2" />
                                        Edit Apartment
                                    </button>
                                    {apartment.bookings_count && apartment.bookings_count > 0 && (
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <div className="flex items-start">
                                                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                                                <p className="text-xs text-yellow-800">
                                                    This apartment has {apartment.bookings_count} active booking(s). Deleting may affect bookings.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewApartment;

